/**
 * Origin allowlist helpers for interceptors that attach credential material
 * (Authorization, x-api-key, id-token, cookies) to outgoing requests.
 */
export class TrustedOriginUtil {
	private static readonly LOCAL_HOSTNAMES = ['localhost', '127.0.0.1'];

	/**
	 * Resolves candidate urls (the application origin, configured service paths) to the set of
	 * origins credential material may be sent to. Unparseable and insecure candidates are dropped.
	 */
	public static getTrustedOrigins(candidates: string[]): string[] {
		return candidates
			.map(candidate => TrustedOriginUtil.toTrustedOrigin(candidate))
			.filter(origin => !!origin);
	}

	public static isTrustedDestination(url: string, trustedOrigins: string[]): boolean {
		const target = TrustedOriginUtil.parse(url);
		return !!target && trustedOrigins.indexOf(target.origin) > -1;
	}

	/**
	 * True when the url resolves to the same origin as basePath and its path sits under the basePath
	 * path. Unlike a substring match, an attacker-controlled url cannot satisfy this by embedding
	 * basePath in its own host, path, query or fragment.
	 */
	public static isUnderBasePath(url: string, basePath: string): boolean {
		const target = TrustedOriginUtil.parse(url);
		const base = TrustedOriginUtil.parse(basePath);
		if (!target || !base) {
			return false;
		}
		const basePathname = base.pathname.replace(/\/$/, '');
		return target.origin === base.origin
			&& (target.pathname === basePathname || target.pathname.indexOf(`${basePathname}/`) === 0);
	}

	private static toTrustedOrigin(candidate: string): string {
		const parsed = TrustedOriginUtil.parse(candidate);
		if (!parsed) {
			return undefined;
		}
		const isLocalHostname = TrustedOriginUtil.LOCAL_HOSTNAMES.indexOf(parsed.hostname) > -1;
		const isSecureOrigin = parsed.protocol === 'https:' || (parsed.protocol === 'http:' && isLocalHostname);
		return isSecureOrigin ? parsed.origin : undefined;
	}

	private static parse(url: string): URL {
		try {
			return new URL(url, window.location.href);
		} catch {
			return undefined;
		}
	}
}
